import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { useLogoForScheme } from "utils/themeContextHelper";
import { Stack } from "components/layout/reusableLayouts";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import AcquirerCheckout from "components/checkout/AcquirerCheckout";
import { CheckoutSessionPayload } from "components/checkout/types";
import PayLinkAmountSection from "components/payLink/PayLinkAmountSection";
import PayLinkPaymentMethodPicker from "components/payLink/PayLinkPaymentMethodPicker";
import PayLinkSummary from "components/payLink/PayLinkSummary";
import TipSelector from "components/payLink/TipSelector";
import FeeCoverCheckbox from "components/payLink/FeeCoverCheckbox";
import MandateConsent from "components/payLink/MandateConsent";
import ContactLoginDialog from "components/auth/ContactLoginDialog";
import PayLinkOutcome from "components/payLink/PayLinkOutcome";
import PayLinkUnavailable from "components/payLink/PayLinkUnavailable";
import { toAttachmentItems } from "utils/attachments/attachmentItems";
import {
  useAttachmentListLabels,
  useAttachmentViewerLabels,
} from "hooks/useAttachmentLabels";
import { useDisplayAttachments } from "hooks/useDisplayAttachments";
import { usePayLinkSession } from "hooks/usePayLinkSession";
import { useContactAuth } from "hooks/useContactAuthContext";
import { Accordion, AttachmentList, TextField } from "@outpave/ds";
import {
  PaymentLinkAmountMode,
  PaymentLinkAvailability,
  PaymentLinkType,
} from "enums/PaymentLink";
import { intervalPeriod } from "utils/billingInterval";

const MANDATE_TEXT_VERSION = "2026-09-16";

const BASE_URL = process.env.REACT_APP_URL;

const PayLink: FC = () => {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  const { contactToken, isContactAuthenticated, logoutContact } =
    useContactAuth();
  const {
    page,
    session,
    quotes,
    quoting,
    loading,
    error,
    verificationRequired,
    loadPage,
    loadPaymentStatus,
    recordView,
    requestQuote,
    startSession,
    repriceSession,
  } = usePayLinkSession(slug, contactToken);

  const attachmentLabels = useAttachmentListLabels();
  const attachmentViewerLabels = useAttachmentViewerLabels();
  const attachmentSources = useMemo(
    () =>
      (page?.attachments || []).map((attachment) => ({
        id: attachment.id,
        assetUrl: attachment.assetUrl,
        extension: attachment.extension,
        name: attachment.filename,
      })),
    [page?.attachments],
  );
  const attachmentFiles = useMemo(
    () => toAttachmentItems(attachmentSources, t("Attachment")),
    [attachmentSources, t],
  );
  const { printAsset, downloadAsset } = useDisplayAttachments({
    allAttachments: attachmentSources,
  });

  const [payerEmail, setPayerEmail] = useState("");
  const [amount, setAmount] = useState<number | null>(null);
  const [paymentMethodType, setPaymentMethodType] = useState("");
  const [feeCoverAccepted, setFeeCoverAccepted] = useState(false);
  const [tipAmount, setTipAmount] = useState(0);
  const [mandateAccepted, setMandateAccepted] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [pendingMethod, setPendingMethod] = useState<string | null>(null);
  const [sessionVersion, setSessionVersion] = useState(0);
  const [loadingMethod, setLoadingMethod] = useState<string | null>(null);
  // Logout bumps it, so the amount and tip pickers drop their own state with the rest.
  const [restarts, setRestarts] = useState(0);

  const isCompleteRoute = location.pathname.endsWith("/complete");
  const returnedPaymentId = new URLSearchParams(location.search).get(
    "payment_intent",
  );
  const [completedStatus, setCompletedStatus] = useState<string | null>(null);
  const clientReferenceId = new URLSearchParams(location.search).get("ref");
  const isPreview = new URLSearchParams(location.search).get("preview") === "1";

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (isPreview) return;

    recordView();
  }, [recordView, isPreview]);

  useEffect(() => {
    if (!isCompleteRoute || !returnedPaymentId) return;

    loadPaymentStatus(returnedPaymentId).then(setCompletedStatus);
  }, [isCompleteRoute, returnedPaymentId, loadPaymentStatus]);

  const paymentLink = page?.paymentLink;
  const payerIdentity = page?.payerIdentity;
  // Only that email reaches the payer's saved methods, so a signed-in payer cannot type another.
  const signedInEmail = isContactAuthenticated
    ? payerIdentity?.payerEmail
    : undefined;
  const allowedMethods = useMemo(
    () => page?.allowedPaymentMethods || [],
    [page?.allowedPaymentMethods],
  );

  useEffect(() => {
    if (paymentLink?.amountMode === PaymentLinkAmountMode.FIXED) {
      setAmount(paymentLink.amount ?? null);
    }
  }, [paymentLink]);

  useEffect(() => {
    if (payerIdentity?.payerEmail) {
      setPayerEmail(payerIdentity.payerEmail);
    }
  }, [payerIdentity]);

  // Keep the preview method separate until selecting a method actually opens a session.
  const previewMethod = paymentMethodType || allowedMethods[0] || "";
  const tableQuote = quotes?.[previewMethod] || null;
  const feeCoverOffer = tableQuote?.feeCoverAmount || 0;

  const previewQuote =
    tableQuote && feeCoverAccepted && tableQuote.totalWithFeeCover != null
      ? { ...tableQuote, totalToCharge: tableQuote.totalWithFeeCover }
      : tableQuote;

  const optimisticQuote =
    previewQuote && tipAmount > 0
      ? {
          ...previewQuote,
          tipAmount,
          totalToCharge: Number(previewQuote.totalToCharge) + tipAmount,
        }
      : previewQuote;

  const activeQuote =
    (loadingMethod ? optimisticQuote : session?.quote) ||
    optimisticQuote ||
    null;

  const checkoutSession: CheckoutSessionPayload | null = useMemo(() => {
    if (!session || !page?.acquirer) return null;

    return {
      acquirer: page.acquirer.acquirer,
      clientSecret: session.clientSecret,
      customerSessionClientSecret: session.customerSessionClientSecret,
      publishableKey: page.acquirer.publishableKey,
      componentType: page.acquirer.componentType,
    };
  }, [session, page]);

  const handleAmountChange = (nextAmount: number | null) => {
    setAmount(nextAmount);
    requestQuote(nextAmount);
  };

  // Signing out, by Logout or by the sign-in expiring, starts the checkout over for the next person on the device.
  const wasAuthenticated = useRef(isContactAuthenticated);
  useEffect(() => {
    const signedOut = wasAuthenticated.current && !isContactAuthenticated;
    wasAuthenticated.current = isContactAuthenticated;
    if (!signedOut) return;

    setPaymentMethodType("");
    setPayerEmail("");
    setMandateAccepted(false);
    setTipAmount(0);
    setFeeCoverAccepted(false);
    if (paymentLink?.amountMode === PaymentLinkAmountMode.PAYER_CHOOSES) {
      handleAmountChange(null);
    }
    setRestarts((count) => count + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isContactAuthenticated]);

  const handleMethodChange = async (nextMethod: string) => {
    if (isSubscription && session) return;
    setPaymentMethodType(nextMethod);
    setLoadingMethod(nextMethod);

    try {
      if (!session) {
        await openSession(nextMethod);
        return;
      }

      // Remount only after repricing; waiting for the unmounted widget would deadlock the switch.
      // A null answer means the session is gone, so there is no method of its to go back to.
      if ((await repriceSession({ paymentMethodType: nextMethod })) === false) {
        setPaymentMethodType(session.paymentMethodType);
      }
    } finally {
      setLoadingMethod(null);
    }
  };

  const handleFeeCoverChange = async (accepted: boolean) => {
    setFeeCoverAccepted(accepted);

    if (session) {
      const repriced = await repriceSession({ feeCoverAccepted: accepted });
      if (repriced) setSessionVersion((version) => version + 1);
    }
  };

  const handleTipChange = async (amount: number) => {
    setTipAmount(amount);

    if (session) {
      const repriced = await repriceSession({ tipAmount: amount });
      if (repriced) setSessionVersion((version) => version + 1);
    }
  };

  const openSession = async (chosenMethod: string) => {
    if (verificationIsRequired && !isContactAuthenticated) {
      setPendingMethod(chosenMethod);
      setVerifyOpen(true);
      return;
    }

    const opened = await startSession({
      payerEmail: payerEmail.trim(),
      amount:
        paymentLink?.amountMode === PaymentLinkAmountMode.PAYER_CHOOSES
          ? amount
          : undefined,
      feeCoverAccepted,
      tipAmount,
      paymentMethodType: chosenMethod,
      clientReferenceId,
      ...(isSubscription
        ? { mandateAccepted, mandateTextVersion: MANDATE_TEXT_VERSION }
        : {}),
      verificationCredential: contactToken,
    });

    // Follow the server's method choice because tips can push card payments above the limit.
    if (opened?.paymentMethodType) {
      setPaymentMethodType(opened.paymentMethodType);
    } else {
      // A failed session request must leave the same method available to retry.
      setPaymentMethodType("");
    }
  };

  useEffect(() => {
    if (!pendingMethod || !isContactAuthenticated) return;

    setPendingMethod(null);
    handleMethodChange(pendingMethod);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingMethod, isContactAuthenticated]);

  const isSubscription = paymentLink?.linkType === PaymentLinkType.SUBSCRIPTION;

  // Recurring and addressed links require a verified payer before opening a session.
  const verificationIsRequired =
    !!payerIdentity?.verificationRequired || verificationRequired;

  const canChooseMethod =
    !!payerEmail.trim() && (!isSubscription || mandateAccepted);

  const isPayerChosen =
    paymentLink?.amountMode === PaymentLinkAmountMode.PAYER_CHOOSES;
  const awaitingAmount =
    isPayerChosen && (amount == null || !quotes) && !session;
  const offeredMethods = session?.allowedPaymentMethods || allowedMethods;
  // A priced amount can still have no method, when it is past every limit the merchant takes.
  const noMethodForAmount =
    !awaitingAmount && !session && !!quotes && offeredMethods.length === 0;

  if (isCompleteRoute) {
    return (
      <PayLinkPage
        onLogout={isContactAuthenticated ? logoutContact : undefined}
      >
        <PayLinkCard>
          <PayLinkOutcome
            status={completedStatus}
            paymentLink={paymentLink}
            companyName={page?.companyDetails?.companyName}
          />
        </PayLinkCard>
      </PayLinkPage>
    );
  }

  if (page && page.availability !== PaymentLinkAvailability.AVAILABLE) {
    return (
      <PayLinkPage
        onLogout={isContactAuthenticated ? logoutContact : undefined}
      >
        <PayLinkCard>
          <PayLinkUnavailable
            availability={page.availability}
            inactiveMessage={page.inactiveMessage}
          />
        </PayLinkCard>
      </PayLinkPage>
    );
  }

  return (
    <PayLinkPage onLogout={isContactAuthenticated ? logoutContact : undefined}>
      {paymentLink && (
        <>
          <PayLinkCard>
            {isPreview && (
              <Alert severity="info" role="status">
                {t("payLink.PreviewNotice")}
              </Alert>
            )}

            <Typography variant="h5">{paymentLink.name}</Typography>
            {paymentLink.description && (
              <Typography variant="body2">{paymentLink.description}</Typography>
            )}

            <PayLinkAmountSection
              key={restarts}
              paymentLink={paymentLink}
              amount={amount}
              onAmountChange={handleAmountChange}
              // A checkout is priced for one amount, so it locks once one starts opening.
              disabled={!!session || !!loadingMethod}
            />

            {!!page?.attachments?.length && (
              <Accordion
                title={t("Attachments")}
                description={t(
                  page.attachments.length === 1
                    ? "{{count}} file"
                    : "{{count}} files",
                  { count: page.attachments.length },
                )}
              >
                <AttachmentList
                  files={attachmentFiles}
                  labels={attachmentLabels}
                  viewer={{ labels: attachmentViewerLabels, filmstrip: true }}
                  layout="grid"
                  onDownload={(item) => item.url && downloadAsset(item.url)}
                  onPrint={(item) => item.url && printAsset(item.url)}
                />
              </Accordion>
            )}
          </PayLinkCard>

          {!session && (
            <PayLinkCard>
              <TextField
                id="pay-link-email"
                fullWidth
                required
                type="email"
                label={t("Email")}
                placeholder="name@example.com"
                value={payerEmail}
                disabled={!!signedInEmail}
                helperText={
                  signedInEmail ? t("payLink.SignedInWithEmail") : undefined
                }
                onChange={(event) => setPayerEmail(event.target.value)}
              />
              <Typography
                variant="caption"
                sx={{ color: "var(--color-text-secondary)" }}
              >
                {t("payLink.NameAndAddressNext")}
              </Typography>

              {!isContactAuthenticated && !verificationIsRequired && (
                <Box>
                  <Button
                    variant="default-soft"
                    disabled={!payerEmail.trim()}
                    onClick={() => setVerifyOpen(true)}
                  >
                    {t("Use a saved payment method")}
                  </Button>
                </Box>
              )}
            </PayLinkCard>
          )}

          <PayLinkCard>
            {isSubscription && activeQuote && !session && (
              <MandateConsent
                companyName={page?.companyDetails?.companyName || ""}
                amount={activeQuote.totalToCharge}
                currency={paymentLink.currency}
                intervalLabel={t(intervalPeriod(paymentLink.interval))}
                maxPayments={paymentLink.maxPayments}
                endsAt={paymentLink.endsAt}
                accepted={mandateAccepted}
                onChange={setMandateAccepted}
              />
            )}

            {/* A payer-chosen link has no methods to offer until its amount is priced. */}
            {awaitingAmount || noMethodForAmount ? (
              <Stack gap="var(--space-2)">
                <Typography variant="subtitle2">
                  {t("Payment method")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "var(--color-text-secondary)" }}
                >
                  {awaitingAmount
                    ? t("payLink.ChooseAmountFirst")
                    : isPayerChosen
                      ? t("payLink.NoMethodTryAnother")
                      : t("payLink.NoMethodForAmount")}
                </Typography>
              </Stack>
            ) : (
              <PayLinkPaymentMethodPicker
                methods={offeredMethods}
                quotes={quotes}
                currency={paymentLink.currency}
                selected={paymentMethodType}
                disabled={
                  !canChooseMethod ||
                  !!loadingMethod ||
                  quoting ||
                  isPreview ||
                  (isSubscription && !!session)
                }
                chosen={!!session}
                renderExpanded={(method) => {
                  // Preview navigation can retain a session, so never mount its payment widget.
                  if (isPreview) return null;

                  if (loadingMethod) {
                    return (
                      <Box sx={{ position: "relative", minHeight: 180 }}>
                        <LoadingWithoutModal
                          open
                          contained
                          curved
                          smallLoading
                          text={t("payLink.LoadingPaymentMethod")}
                        />
                      </Box>
                    );
                  }

                  if (!checkoutSession) return null;

                  return (
                    <AcquirerCheckout
                      // A method switch needs a fresh widget bound to the repriced intent.
                      key={method}
                      session={checkoutSession}
                      returnUrl={`${BASE_URL}/pay/${slug}/complete`}
                      billingDefaults={{
                        firstName: firstNameOf(payerIdentity?.payerName),
                        lastName: lastNameOf(payerIdentity?.payerName),
                      }}
                      sessionVersion={sessionVersion}
                      submitLabel={t("Pay now")}
                    />
                  );
                }}
                onSelect={handleMethodChange}
              />
            )}

            {paymentLink.tip?.enabled && (
              <TipSelector
                key={restarts}
                subtotal={activeQuote?.intendedAmount || 0}
                currency={paymentLink.currency}
                presetPercentages={paymentLink.tip.presetPercentages}
                value={tipAmount}
                disabled={!!loadingMethod}
                onChange={handleTipChange}
              />
            )}

            {/* The subscription mandate freezes the amount once its session exists. */}
            {page?.feeCoverEnabled && !(isSubscription && session) && (
              <FeeCoverCheckbox
                amount={feeCoverOffer}
                currency={paymentLink.currency}
                checked={feeCoverAccepted}
                onChange={handleFeeCoverChange}
              />
            )}

            {activeQuote && (
              <PayLinkSummary
                quote={activeQuote}
                currency={paymentLink.currency}
              />
            )}

            {error && (
              <Typography
                variant="body2"
                sx={{ color: "var(--color-text-danger)" }}
              >
                {t(error)}
              </Typography>
            )}
          </PayLinkCard>
        </>
      )}

      <ContactLoginDialog
        open={verifyOpen}
        email={payerEmail.trim()}
        paymentLinkSlug={slug}
        onAuthenticated={() => setVerifyOpen(false)}
        onClose={() => {
          setVerifyOpen(false);
          setPendingMethod(null);
          setPaymentMethodType("");
        }}
      />

      <LoadingWithoutModal open={loading} />
    </PayLinkPage>
  );
};

function firstNameOf(fullName?: string) {
  return fullName?.trim().split(/\s+/)[0] || "";
}

function lastNameOf(fullName?: string) {
  const parts = fullName?.trim().split(/\s+/) || [];

  return parts.length > 1 ? parts.slice(1).join(" ") : "";
}

const PayLinkPage: React.FC<{
  /** Present for a verified payer, as on the invoice page. */
  onLogout?: () => void;
  children: React.ReactNode;
}> = ({ onLogout, children }) => {
  const { t } = useTranslation();
  const platformLogo = useLogoForScheme();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        backgroundColor: "var(--color-bg-page)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          padding: "var(--space-6)",
        }}
      >
        <Box
          component="img"
          src={platformLogo}
          alt=""
          draggable={false}
          sx={{ height: 40, width: "auto", maxWidth: 200 }}
        />
      </Box>

      {onLogout && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            padding: "var(--space-6)",
          }}
        >
          <Button variant="primary-outlined" onClick={onLogout}>
            {t("Logout")}
          </Button>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          paddingTop: "var(--space-12)",
          paddingBottom: "var(--space-8)",
          paddingX: "var(--space-4)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-5)",
            width: "100%",
            maxWidth: 500,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

const PayLinkCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Card>
    <CardContent
      sx={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
    >
      {children}
    </CardContent>
  </Card>
);

export default PayLink;
